import { Box, Button } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Vex from "vexflow";
import * as Tone from "tone";
import { DEFAUL_MIDI_KEY, PianoKeyboard } from "../PianoKeyboard/PianoKeyboard";
import { SheetMusic } from "../SheetMusic/SheetMusic";
import { createRandomVexflowMelody } from "../../utils/createRandomMelody";
import { useNotesMicrophoneDetect } from "../../hooks/useNotesMicrophoneDetect";
import { ISheetMusicNoteTrainerSettings, MAX_MIDI, MIN_MIDI, SheetMusicNoteTrainerSettings } from "./SheetMusicNoteTrainerSettings";

const displayPianoRange: [number, number] = [MIN_MIDI, MAX_MIDI]

const parseVexNoteToMidi = (vexNote: string): number[] => {
    // Извлекаем ноты из строки VexFlow (например "C#4/q" или "(C4 E4)/q")
    const matches = vexNote.match(/[A-G][#b]?[0-9]/g);
    if (!matches) return [];
    return matches.map(note => Tone.Frequency(note).toMidi());
}

export const SheetMusicNoteTrainer: React.FC = () => {
    const scoreRef = useRef<HTMLDivElement>(null);
    const synthRef = useRef<Tone.Sampler | null>(null);
    const [settings, setSettings] = useState<ISheetMusicNoteTrainerSettings | null>(null);
    const [currentNotes, setCurrentNotes] = useState<number[]>([]);
    const [melody, setMelody] = useState<string[]>([]);
    const [activeNoteIndex, setActiveNoteIndex] = useState(0);
    const [isMuted, setIsMuted] = useState(false);

    useEffect(() => {
        synthRef.current = new Tone.Sampler({
            urls: {
                A0: "A0.mp3", C1: "C1.mp3", "D#1": "Ds1.mp3", "F#1": "Fs1.mp3", A1: "A1.mp3",
                C2: "C2.mp3", "D#2": "Ds2.mp3", "F#2": "Fs2.mp3", A2: "A2.mp3",
                C3: "C3.mp3", "D#3": "Ds3.mp3", "F#3": "Fs3.mp3", A3: "A3.mp3",
                C4: "C4.mp3", "D#4": "Ds4.mp3", "F#4": "Fs4.mp3", A4: "A4.mp3",
                C5: "C5.mp3", "D#5": "Ds5.mp3", "F#5": "Fs5.mp3", A5: "A5.mp3",
                C6: "C6.mp3", "D#6": "Ds6.mp3", "F#6": "Fs6.mp3", A6: "A6.mp3",
                C7: "C7.mp3", "D#7": "Ds7.mp3", "F#7": "Fs7.mp3", A7: "A7.mp3",
                C8: "C8.mp3"
            },
            baseUrl: "https://tonejs.github.io/audio/salamander/",
        }).toDestination();
        return () => {
            synthRef.current?.dispose();
        };
    }, []);

    const handlePlayNote = async (keys: number[], muted?: boolean) => {
        await Tone.start();
        setCurrentNotes(keys);

        if (melody.length > activeNoteIndex && keys.length > 0) {
            const targetNoteStr = melody[activeNoteIndex];
            const targetMidis = parseVexNoteToMidi(targetNoteStr);
            
            // Проверяем, содержит ли нажатая клавиша нужную ноту (или является частью аккорда)
            const isCorrect = targetMidis.every(target => keys.includes(target));
            
            if (isCorrect) {
                if (activeNoteIndex + 1 >= melody.length) {
                    setMelody(createRandomVexflowMelody({ range: settings!.range })); // Можете также пробросить keyId, если утилита поддерживает тональность
                    setActiveNoteIndex(0);
                } else {
                    setActiveNoteIndex(prev => prev + 1);
                }
            }
        }

        if (synthRef.current && !isMuted && !muted) {
            keys.forEach((key) => {
                synthRef.current?.triggerAttackRelease(Tone.Frequency(key, "midi").toNote(), "8n");
            });
        }
    };

    const handleStart = (newSettings: ISheetMusicNoteTrainerSettings) => {
        setSettings(newSettings);
        // Генерируем первую мелодию, используя выбранный диапазон (и, если нужно, тональность)
        setMelody(createRandomVexflowMelody({ range: newSettings.range }));
    };

    const pianoRange: [number, number] = useMemo(() => {
        if (!settings?.range) {
            return displayPianoRange;
        }
        
        let minRange = Math.max(settings.range[0], displayPianoRange[0]);
        let maxRange = Math.min(settings.range[1], displayPianoRange[1]);
        
        minRange = Math.min(minRange, DEFAUL_MIDI_KEY);
        maxRange = Math.max(maxRange, DEFAUL_MIDI_KEY + 4);

        const allowedKeys = [0, 4, 5, 11]; // C=0, E=4, F=5, B=11
        while (!allowedKeys.includes(minRange % 12) && minRange > displayPianoRange[0]) {
            minRange--;
        }
        while (!allowedKeys.includes(maxRange % 12) && maxRange < displayPianoRange[1]) {
            maxRange++;
        }

        return [minRange, maxRange];
    }, [settings])

    if (!settings) {
        return <SheetMusicNoteTrainerSettings onSuccess={handleStart} />;
    }

    return <Box>
        <SheetMusic currentNotes={currentNotes} currentMelody={melody} activeNoteIndex={activeNoteIndex} />
        <PianoKeyboard
            onPush={handlePlayNote}
            range={pianoRange}
            isMuted={isMuted}
            onToggleMute={() => setIsMuted(prev => !prev)}
        />
    </Box>
}